/**
 * @fileoverview Gather Information View
 *
 * Purpose: Thin orchestrator for the Gather Information phase. Owns the
 * deep-link (`?documentId=…`) sync and document selection state, and
 * delegates the documents workspace to the Finder-based LibraryView.
 *
 * @component
 * @example
 * <GatherView jobId={jobId} />
 */

import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { LibraryView } from "./library/LibraryView";

interface GatherViewProps {
  jobId: Id<"jobs">;
}

/**
 * Main view for the Gather Information phase. Renders the documents
 * library only — Summaries now lives on its own top-level route.
 */
export function GatherView({ jobId }: GatherViewProps) {
  const [selectedDocumentId, setSelectedDocumentId] =
    useState<Id<"documents"> | null>(null);

  // Universal search / command palette can deep-link a document open
  // via `?documentId=…`. Sync that into local state on mount and
  // whenever it changes, and clear the param when the user closes
  // the panel so a stale URL doesn't keep re-opening it.
  const searchParams = useSearch({
    from: "/_app/_auth/dashboard/_layout/jobs/$jobId/gather",
  });
  const urlDocumentId = searchParams.documentId;
  const navigate = useNavigate({
    from: "/dashboard/jobs/$jobId/gather",
  });

  useEffect(() => {
    if (urlDocumentId) {
      setSelectedDocumentId(urlDocumentId as Id<"documents">);
    }
  }, [urlDocumentId]);

  const handleCloseDetail = useCallback(() => {
    setSelectedDocumentId(null);
    if (urlDocumentId) {
      void navigate({
        to: "/dashboard/jobs/$jobId/gather",
        params: { jobId },
        search: {},
        replace: true,
      });
    }
  }, [navigate, jobId, urlDocumentId]);

  const handleSelectDocument = useCallback(
    (id: Id<"documents"> | null) => {
      if (id === null) handleCloseDetail();
      else setSelectedDocumentId(id);
    },
    [handleCloseDetail],
  );

  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );

  const isViewer = job?.userRole === "viewer";

  return (
    // The dashboard shell lets pages grow (other routes scroll the body),
    // so the Library clamps itself to the viewport space below the sticky
    // app header (h-16 = 4rem): the Finder fills it, its internal views
    // scroll, and the status bar stays visible.
    <div className="flex h-[calc(100svh-4rem)] min-h-0 flex-col overflow-hidden p-6">
      <LibraryView
        jobId={jobId}
        isViewer={isViewer}
        selectedDocumentId={selectedDocumentId}
        onSelectDocument={handleSelectDocument}
      />
    </div>
  );
}
